import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { productService } from '../shared/services';
import { productToCandleProduct } from '../shared/utils';
import { evaluateMaster3DTimeline } from '../utils/masterTimeline';
import { logAuditState } from '../utils/auditLogger';

interface SceneCanvasProps {
  scrollProgress: number; // 0.0 to 1.0
  activeCandleIndex: number;
  onCandleSelect?: (index: number) => void;
  isMobile?: boolean;
}

export const SceneCanvas: React.FC<SceneCanvasProps> = React.memo(({
  scrollProgress,
  activeCandleIndex,
  isMobile = false,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);

  // Store scrollProgress and activeCandleIndex in refs to avoid re-creating Three.js scene on scroll
  const scrollProgressRef = useRef(scrollProgress);
  const smoothedProgressRef = useRef(scrollProgress);
  const activeCandleIndexRef = useRef(activeCandleIndex);

  useEffect(() => {
    scrollProgressRef.current = scrollProgress;
  }, [scrollProgress]);

  useEffect(() => {
    activeCandleIndexRef.current = activeCandleIndex;
  }, [activeCandleIndex]);

  // Store Three.js instances in ref
  const threeRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    flameMaterial: THREE.ShaderMaterial;
    sparkParticles: THREE.Points;
    sparkMat: THREE.PointsMaterial;
    dustParticles: THREE.Points;
    petalParticles: THREE.Points;
    petalMat: THREE.PointsMaterial;
    flameLight: THREE.PointLight;
    volumetricLight: THREE.SpotLight;
    candleMeshGroup: THREE.Group;
    workshopGroup: THREE.Group;
    ingredientsGroup: THREE.Group[];
    galleryGroup: THREE.Group;
    unboxingGroup: THREE.Group;
    currentLookAt: THREE.Vector3;
    targetLookAt: THREE.Vector3;
    candleGlassMeshes: THREE.Mesh[];
  } | null>(null);

  useEffect(() => {
    logAuditState('5. SceneCanvas Mounted');
    const container = mountRef.current;
    if (!container) return;

    // 1. Setup Three Scene, Camera & Renderer
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x0a0806, 0.035);

    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      100
    );
    camera.position.set(0, 0, 8);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.0 : 1.5));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.25;
    renderer.shadowMap.enabled = !isMobile;
    if (!isMobile) {
      renderer.shadowMap.type = THREE.PCFShadowMap;
    }

    // Clear previous canvas
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    container.appendChild(renderer.domElement);

    // 2. Lighting Setup
    const ambientLight = new THREE.AmbientLight(0xffeedd, 0.2);
    scene.add(ambientLight);

    const flameLight = new THREE.PointLight(0xffaa44, 2.5, 12);
    flameLight.position.set(0, 0.4, 0);
    flameLight.castShadow = false;
    scene.add(flameLight);

    const volumetricLight = new THREE.SpotLight(
      0xffe0b2,
      2.5,
      20,
      Math.PI / 5,
      0.6,
      1
    );
    volumetricLight.position.set(0, 8, 4);
    volumetricLight.target.position.set(0, 0, 0);
    volumetricLight.castShadow = false;
    scene.add(volumetricLight);
    scene.add(volumetricLight.target);

    // Warm Rim / Fill Lights
    const rimLight = new THREE.DirectionalLight(0xcc9966, 0.5);
    rimLight.position.set(0, 4, -5);
    scene.add(rimLight);

    // 3. Flame Custom Shader
    const flameVertexShader = `
      varying vec2 vUv;
      varying vec3 vPosition;
      uniform float uTime;
      
      void main() {
        vUv = uv;
        vPosition = position;
        vec3 pos = position;
        
        // Gentle flame flicker displacement
        float noise = sin(pos.y * 8.0 + uTime * 6.0) * 0.04 * vUv.y;
        pos.x += noise;
        pos.z += cos(pos.y * 6.0 + uTime * 5.0) * 0.03 * vUv.y;
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `;

    const flameFragmentShader = `
      varying vec2 vUv;
      varying vec3 vPosition;
      uniform float uTime;
      uniform vec3 uColorCore;
      uniform vec3 uColorOuter;
      
      void main() {
        // Flame teardrop shape gradient
        float centerDist = length(vUv - vec2(0.5, 0.3));
        float verticalFactor = pow(vUv.y, 0.7);
        float alpha = (1.0 - smoothstep(0.0, 0.45, length(vec2((vUv.x - 0.5) * 2.2, vUv.y - 0.3)))) * verticalFactor;
        
        // Inner core intensity
        float core = smoothstep(0.35, 0.0, length(vUv - vec2(0.5, 0.25)));
        vec3 finalColor = mix(uColorOuter, uColorCore, core + 0.3);
        
        // Flicker intensity modulation
        float flicker = 0.85 + 0.15 * sin(uTime * 12.0) + 0.1 * cos(uTime * 19.0);
        
        gl_FragColor = vec4(finalColor * flicker * 2.2, alpha * flicker);
      }
    `;

    const flameMaterial = new THREE.ShaderMaterial({
      vertexShader: flameVertexShader,
      fragmentShader: flameFragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uColorCore: { value: new THREE.Color(0xfff0aa) },
        uColorOuter: { value: new THREE.Color(0xff6600) },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    // 4. Create Master Candle Mesh Group
    const candleMeshGroup = new THREE.Group();

    // Candle Amber Glass Jar
    const jarGeo = new THREE.CylinderGeometry(0.9, 0.85, 2.2, isMobile ? 24 : 48, 1, true);
    const jarMat = new THREE.MeshPhysicalMaterial({
      color: 0x221308,
      transmission: isMobile ? 0.0 : 0.82,
      opacity: isMobile ? 0.88 : 1.0,
      transparent: true,
      roughness: 0.35,
      metalness: 0.0,
      ior: 1.3,
      thickness: 0.15,
      clearcoat: 0.0,
      clearcoatRoughness: 0.5,
    });
    const jarMesh = new THREE.Mesh(jarGeo, jarMat);
    jarMesh.position.y = -0.1;
    candleMeshGroup.add(jarMesh);

    // Glass Base Cap
    const baseGeo = new THREE.CylinderGeometry(0.85, 0.85, 0.15, isMobile ? 24 : 48);
    const baseMesh = new THREE.Mesh(baseGeo, jarMat);
    baseMesh.position.y = -1.18;
    candleMeshGroup.add(baseMesh);

    // Cream Soy Wax Fill
    const waxGeo = new THREE.CylinderGeometry(0.82, 0.82, 1.7, isMobile ? 20 : 32);
    const waxMat = new THREE.MeshStandardMaterial({
      color: 0xf3e8d6,
      roughness: 0.4,
      metalness: 0.05,
    });
    const waxMesh = new THREE.Mesh(waxGeo, waxMat);
    waxMesh.position.y = -0.3;
    candleMeshGroup.add(waxMesh);

    // Organic Cotton Wick
    const wickGeo = new THREE.CylinderGeometry(0.02, 0.02, 0.4, 12);
    const wickMat = new THREE.MeshStandardMaterial({
      color: 0x111111,
      roughness: 0.9,
    });
    const wickMesh = new THREE.Mesh(wickGeo, wickMat);
    wickMesh.position.y = 0.65;
    candleMeshGroup.add(wickMesh);

    // Flame Mesh
    const flameGeo = new THREE.ConeGeometry(0.18, 0.65, isMobile ? 18 : 32);
    flameGeo.translate(0, 0.32, 0);
    const flameMesh = new THREE.Mesh(flameGeo, flameMaterial);
    flameMesh.position.y = 0.8;
    candleMeshGroup.add(flameMesh);

    // Luxury Label on Jar
    const labelGeo = new THREE.CylinderGeometry(
      0.91,
      0.91,
      1.1,
      isMobile ? 20 : 32,
      1,
      true,
      -Math.PI / 3,
      (2 * Math.PI) / 3
    );
    const labelCanvas = document.createElement('canvas');
    labelCanvas.width = isMobile ? 256 : 512;
    labelCanvas.height = isMobile ? 128 : 256;
    const ctx = labelCanvas.getContext('2d');
    if (ctx) {
      const w = labelCanvas.width;
      const h = labelCanvas.height;
      ctx.fillStyle = '#f7f3ed';
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = '#3d2e1e';
      ctx.lineWidth = isMobile ? 2 : 4;
      ctx.strokeRect(6, 6, w - 12, h - 12);

      ctx.fillStyle = '#21150c';
      ctx.font = isMobile ? '600 16px serif' : '600 32px serif';
      ctx.textAlign = 'center';
      ctx.fillText('N O O R', w / 2, h * 0.35);

      ctx.font = isMobile ? '300 9px sans-serif' : '300 16px sans-serif';
      ctx.fillStyle = '#6e5d4f';
      ctx.fillText('HANDMADE LUXURY CANDLE', w / 2, h * 0.49);

      ctx.font = isMobile ? 'italic 10px serif' : 'italic 18px serif';
      ctx.fillStyle = '#3d2e1e';
      ctx.fillText('Light a Moment', w / 2, h * 0.67);

      ctx.font = isMobile ? '7px sans-serif' : '12px sans-serif';
      ctx.fillStyle = '#8a7767';
      ctx.fillText('280g • Rashmi Atelier', w / 2, h * 0.82);
    }

    const labelTexture = new THREE.CanvasTexture(labelCanvas);
    const labelMat = new THREE.MeshStandardMaterial({
      map: labelTexture,
      roughness: 0.6,
      side: THREE.DoubleSide,
    });
    const labelMesh = new THREE.Mesh(labelGeo, labelMat);
    labelMesh.position.y = -0.1;
    candleMeshGroup.add(labelMesh);

    scene.add(candleMeshGroup);

    // 5. Spark Particles (Scene 1 & 2)
    const sparkCount = isMobile ? 18 : 120;
    const sparkGeo = new THREE.BufferGeometry();
    const sparkPos = new Float32Array(sparkCount * 3);
    const sparkVel = new Float32Array(sparkCount * 3);

    for (let i = 0; i < sparkCount; i++) {
      sparkPos[i * 3] = (Math.random() - 0.5) * 1.5;
      sparkPos[i * 3 + 1] = Math.random() * 2;
      sparkPos[i * 3 + 2] = (Math.random() - 0.5) * 1.5;

      sparkVel[i * 3] = (Math.random() - 0.5) * 0.01;
      sparkVel[i * 3 + 1] = Math.random() * 0.02 + 0.005;
      sparkVel[i * 3 + 2] = (Math.random() - 0.5) * 0.01;
    }

    sparkGeo.setAttribute(
      'position',
      new THREE.BufferAttribute(sparkPos, 3)
    );

    const sparkMat = new THREE.PointsMaterial({
      color: 0xffb703,
      size: 0.08,
      transparent: true,
      blending: THREE.AdditiveBlending,
      opacity: 0.9,
    });
    const sparkParticles = new THREE.Points(sparkGeo, sparkMat);
    scene.add(sparkParticles);

    // 6. Dust Particles (Atmospheric Ambient Motives)
    const dustCount = isMobile ? 35 : 350;
    const dustGeo = new THREE.BufferGeometry();
    const dustPos = new Float32Array(dustCount * 3);

    for (let i = 0; i < dustCount; i++) {
      dustPos[i * 3] = (Math.random() - 0.5) * 16;
      dustPos[i * 3 + 1] = (Math.random() - 0.5) * 12;
      dustPos[i * 3 + 2] = (Math.random() - 0.5) * 16;
    }

    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({
      color: 0xffda9e,
      size: 0.04,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
    });
    const dustParticles = new THREE.Points(dustGeo, dustMat);
    scene.add(dustParticles);

    // 7. Floating Petal Particles (Rose & Fragrance World Scene 9)
    const petalCount = isMobile ? 12 : 80;
    const petalGeo = new THREE.BufferGeometry();
    const petalPos = new Float32Array(petalCount * 3);

    for (let i = 0; i < petalCount; i++) {
      petalPos[i * 3] = (Math.random() - 0.5) * 10;
      petalPos[i * 3 + 1] = Math.random() * 8 - 2;
      petalPos[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }

    petalGeo.setAttribute('position', new THREE.BufferAttribute(petalPos, 3));
    const petalMat = new THREE.PointsMaterial({
      color: 0xe63946,
      size: 0.12,
      transparent: true,
      opacity: 0,
      blending: THREE.NormalBlending,
    });
    const petalParticles = new THREE.Points(petalGeo, petalMat);
    scene.add(petalParticles);

    // 8. Rashmi's Workshop Props (Scene 6)
    const workshopGroup = new THREE.Group();
    workshopGroup.position.set(0, -10, 0); // Hidden by default

    // Wooden Table Top
    const tableGeo = new THREE.BoxGeometry(6, 0.2, 4);
    const tableMat = new THREE.MeshStandardMaterial({
      color: 0x3d271d,
      roughness: 0.8,
    });
    const tableMesh = new THREE.Mesh(tableGeo, tableMat);
    tableMesh.position.set(0, -1.2, 0);
    workshopGroup.add(tableMesh);

    // Glass Pouring Pitcher
    const pitcherGeo = new THREE.CylinderGeometry(0.4, 0.5, 1.2, 24);
    const pitcherMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      transmission: 0.9,
      roughness: 0.1,
    });
    const pitcherMesh = new THREE.Mesh(pitcherGeo, pitcherMat);
    pitcherMesh.position.set(-1.8, -0.5, 0.5);
    workshopGroup.add(pitcherMesh);

    // Essential Oil Apothecary Jars (Shared Geometry & Material)
    const smallJarGeo = new THREE.CylinderGeometry(0.15, 0.15, 0.5, 16);
    const smallJarMat = new THREE.MeshStandardMaterial({ color: 0x211812, roughness: 0.3 });
    [-0.8, -0.4, 0, 0.4].forEach((xOffset) => {
      const jar = new THREE.Mesh(smallJarGeo, smallJarMat);
      jar.position.set(xOffset, -0.85, -0.8);
      workshopGroup.add(jar);
    });

    scene.add(workshopGroup);

    // 9. Floating Ingredients Spheres (Scene 7 - Shared Geometry)
    const ingredientData = [
      { color: 0xf3e8d6, label: 'Soy Wax' },
      { color: 0x111111, label: 'Cotton Wick' },
      { color: 0x3d2314, label: 'Vanilla Bean' },
      { color: 0x26140a, label: 'Arabica Coffee' },
      { color: 0x7b2cbf, label: 'Provence Lavender' },
      { color: 0xc1121f, label: 'Damask Rose' },
      { color: 0xbc6c25, label: 'Sandalwood' },
    ];

    const sharedSphereGeo = new THREE.SphereGeometry(0.45, 32, 32);
    const ingredientsGroup: THREE.Group[] = [];
    ingredientData.forEach((item, idx) => {
      const ingGroup = new THREE.Group();
      const angle = (idx / ingredientData.length) * Math.PI * 2;
      ingGroup.position.set(
        Math.cos(angle) * 3.2,
        Math.sin(idx * 1.5) * 0.8 - 20, // Hidden below initially
        Math.sin(angle) * 3.2
      );

      const sphereMesh = new THREE.Mesh(
        sharedSphereGeo,
        new THREE.MeshPhysicalMaterial({
          color: item.color,
          roughness: 0.3,
          clearcoat: 0.8,
        })
      );
      ingGroup.add(sphereMesh);
      scene.add(ingGroup);
      ingredientsGroup.push(ingGroup);
    });

    // 10. Masterpiece Gallery Candles Array (Scene 10 - Shared Geometries)
    const galleryGroup = new THREE.Group();
    galleryGroup.position.set(0, -40, 0);

    const candleGlassMeshes: THREE.Mesh[] = [];
    const galleryJarGeo = new THREE.CylinderGeometry(1.0, 0.95, 2.4, 32);
    const galleryWaxGeo = new THREE.CylinderGeometry(0.92, 0.92, 1.8, 32);
    const galleryWickGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.45, 12);
    const galleryFlameGeo = new THREE.ConeGeometry(0.2, 0.7, 24);

    const activeCandles = productService.getActive().map(productToCandleProduct);
    activeCandles.forEach((candle, idx) => {
      const candleContainer = new THREE.Group();
      candleContainer.position.set(idx * 5, 0, 0);

      const gJar = new THREE.Mesh(
        galleryJarGeo,
        new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(candle.colorHex),
          transmission: isMobile ? 0.0 : 0.8,
          opacity: isMobile ? 0.85 : 1.0,
          transparent: true,
          roughness: 0.2,
          ior: 1.5,
        })
      );
      candleContainer.add(gJar);
      candleGlassMeshes.push(gJar);

      const gWax = new THREE.Mesh(
        galleryWaxGeo,
        new THREE.MeshStandardMaterial({ color: 0xf4ecd8, roughness: 0.4 })
      );
      gWax.position.y = -0.2;
      candleContainer.add(gWax);

      const gWick = new THREE.Mesh(
        galleryWickGeo,
        new THREE.MeshStandardMaterial({ color: 0x111111 })
      );
      gWick.position.y = 0.8;
      candleContainer.add(gWick);

      // Flame for gallery candle
      const gFlame = new THREE.Mesh(galleryFlameGeo, flameMaterial);
      gFlame.position.y = 1.05;
      candleContainer.add(gFlame);

      galleryGroup.add(candleContainer);
    });
    scene.add(galleryGroup);

    // 11. Unboxing Gift Box Group (Scene 12)
    const unboxingGroup = new THREE.Group();
    unboxingGroup.position.set(0, -60, 0);

    // Box Base
    const boxBase = new THREE.Mesh(
      new THREE.BoxGeometry(2.2, 1.4, 2.2),
      new THREE.MeshStandardMaterial({ color: 0x231812, roughness: 0.5 })
    );
    unboxingGroup.add(boxBase);

    // Box Lid (Lifting)
    const boxLid = new THREE.Mesh(
      new THREE.BoxGeometry(2.28, 0.4, 2.28),
      new THREE.MeshStandardMaterial({ color: 0x2d2018, roughness: 0.4 })
    );
    boxLid.position.y = 0.8;
    unboxingGroup.add(boxLid);

    scene.add(unboxingGroup);

    // Pre-warm WebGL shader pipeline
    renderer.compile(scene, camera);

    // Store references
    threeRef.current = {
      scene,
      camera,
      renderer,
      flameMaterial,
      sparkParticles,
      sparkMat,
      dustParticles,
      petalParticles,
      petalMat,
      flameLight,
      volumetricLight,
      candleMeshGroup,
      workshopGroup,
      ingredientsGroup,
      galleryGroup,
      unboxingGroup,
      currentLookAt: new THREE.Vector3(0, 0, 0),
      targetLookAt: new THREE.Vector3(0, 0, 0),
      candleGlassMeshes,
    };

    // Resize Handler
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // 12. Optimized Lifecycle Render Loop (Viewport + Page Visibility Aware)
    let animationFrameId: number = 0;
    let isLoopRunning = false;
    let isPageVisible = !document.hidden;
    let isIntersecting = true;
    const startTime = performance.now();
    let lastTime = performance.now();

    const startLoop = () => {
      if (!isLoopRunning && isPageVisible && isIntersecting) {
        isLoopRunning = true;
        lastTime = performance.now();
        renderLoop();
      }
    };

    const stopLoop = () => {
      if (isLoopRunning) {
        isLoopRunning = false;
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = 0;
        }
      }
    };

    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden;
      if (isPageVisible && isIntersecting) {
        startLoop();
      } else {
        stopLoop();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        isIntersecting = entry ? entry.isIntersecting : true;
        if (isPageVisible && isIntersecting) {
          startLoop();
        } else {
          stopLoop();
        }
      },
      { threshold: 0 }
    );

    observer.observe(container);

    const renderLoop = () => {
      if (!isLoopRunning) return;
      animationFrameId = requestAnimationFrame(renderLoop);

      const now = performance.now();
      const delta = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const elapsedTime = (now - startTime) / 1000;

      if (threeRef.current) {
        const {
          scene,
          camera,
          renderer,
          flameMaterial,
          sparkParticles,
          sparkMat,
          dustParticles,
          petalParticles,
          petalMat,
          flameLight,
          candleMeshGroup,
          workshopGroup,
          ingredientsGroup,
          galleryGroup,
          unboxingGroup,
          currentLookAt,
        } = threeRef.current;

        // Update Flame shader uniform
        flameMaterial.uniforms.uTime.value = elapsedTime;

        // Frame-rate independent exponential smoothing of scroll progress for liquid-smooth transitions
        smoothedProgressRef.current += (scrollProgressRef.current - smoothedProgressRef.current) * (1 - Math.exp(-14 * delta));
        const p = smoothedProgressRef.current;
        const currentActiveIdx = activeCandleIndexRef.current;

        const {
          desiredCamPos,
          desiredLookAt,
          desiredCandlePos,
          desiredWorkshopPos,
          desiredGalleryPos,
          desiredUnboxingPos,
          candleScale,
          flameIntensity,
          sparkOpacity,
          petalOpacity,
          lidY,
          lidRotZ,
        } = evaluateMaster3DTimeline(p, elapsedTime, currentActiveIdx, isMobile);

        // Apply material and object state from Master Timeline
        if (sparkMat.opacity !== sparkOpacity) sparkMat.opacity = sparkOpacity;
        if (flameLight.intensity !== flameIntensity) flameLight.intensity = flameIntensity;
        if (petalMat.opacity !== petalOpacity) petalMat.opacity = petalOpacity;
        candleMeshGroup.scale.setScalar(candleScale);

        // Animate Sparks only when visible
        if (sparkOpacity > 0.02) {
          const posAttr = sparkParticles.geometry.attributes.position as THREE.BufferAttribute;
          const positions = posAttr.array as Float32Array;
          for (let i = 0; i < sparkCount; i++) {
            positions[i * 3 + 1] += 0.008 + Math.sin(elapsedTime + i) * 0.002;
            if (positions[i * 3 + 1] > 2.5) {
              positions[i * 3 + 1] = 0;
              positions[i * 3] = (Math.random() - 0.5) * 1.2;
              positions[i * 3 + 2] = (Math.random() - 0.5) * 1.2;
            }
          }
          posAttr.needsUpdate = true;
        }

        // Gentle rotate candle
        candleMeshGroup.rotation.y = Math.sin(elapsedTime * (isMobile ? 0.15 : 0.3)) * (isMobile ? 0.1 : 0.2);

        if (unboxingGroup.children[1]) {
          const lid = unboxingGroup.children[1];
          if (lid.position.y !== lidY) lid.position.y = lidY;
          if (lid.rotation.z !== lidRotZ) lid.rotation.z = lidRotZ;
        }

        if (p >= 0.42 && p < 0.50) {
          ingredientsGroup.forEach((ing, i) => {
            const targetY = Math.sin(elapsedTime + i) * 0.3;
            ing.position.y += (targetY - ing.position.y) * 0.1;
            ing.rotation.y = elapsedTime * 0.5;
          });
        } else {
          ingredientsGroup.forEach((ing) => {
            if (Math.abs(ing.position.y - (-30)) > 0.01) {
              ing.position.y += (-30 - ing.position.y) * 0.1;
            }
          });
        }

        // Cinematic frame-rate independent camera & 3D model dampening lerp
        const lerpFactor = 1 - Math.exp(-6 * delta);

        camera.position.lerp(desiredCamPos, lerpFactor);
        currentLookAt.lerp(desiredLookAt, lerpFactor);
        camera.lookAt(currentLookAt);

        candleMeshGroup.position.lerp(desiredCandlePos, lerpFactor);
        workshopGroup.position.lerp(desiredWorkshopPos, lerpFactor);
        galleryGroup.position.lerp(desiredGalleryPos, lerpFactor);
        unboxingGroup.position.lerp(desiredUnboxingPos, lerpFactor);

        // Render
        renderer.render(scene, camera);
      }
    };

    startLoop();

    return () => {
      stopLoop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);

      // Deep GPU resource disposal
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Points) {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach((m) => {
                if (m.map) m.map.dispose();
                m.dispose();
              });
            } else {
              if (obj.material.map) obj.material.map.dispose();
              obj.material.dispose();
            }
          }
        }
      });

      labelTexture.dispose();
      renderer.dispose();
    };
  }, [isMobile]);

  return (
    <div
      ref={mountRef}
      className="fixed inset-0 z-0 bg-[#070504] overflow-hidden pointer-events-none"
    />
  );
});
